import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2, Upload as UploadIcon, X, CheckCircle2, Camera, FileText,
    Landmark, Receipt as ReceiptIcon, FileSpreadsheet, Mail, FileBox, Users, Layers, Copy, ArrowLeft,
    Contact, QrCode, MapPin, LifeBuoy, AlertTriangle, Plus,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

import { api } from "@/lib/api";
import { formatApiError, useAuth } from "@/lib/auth";
import { fileToBase64 } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

// 7 tiles — order matches the spec (most-common first).
const CATEGORIES = [
    { key: "bank_statement", label: "Bank statement", icon: Landmark, hint: "ANZ, ASB, BNZ, Westpac, Kiwibank…", tone: "bg-emerald-50 border-emerald-200 text-emerald-900" },
    { key: "receipt", label: "Receipt", icon: ReceiptIcon, hint: "Snap with your camera", tone: "bg-amber-50 border-amber-200 text-amber-900" },
    { key: "invoice", label: "Invoice", icon: FileText, hint: "Customer or supplier invoice", tone: "bg-sky-50 border-sky-200 text-sky-900" },
    { key: "ird_letter", label: "IRD letter", icon: Mail, hint: "Anything from Inland Revenue", tone: "bg-violet-50 border-violet-200 text-violet-900" },
    { key: "gst", label: "GST documents", icon: FileSpreadsheet, hint: "GST return, GST101A, workings", tone: "bg-rose-50 border-rose-200 text-rose-900" },
    { key: "payroll", label: "Payroll", icon: Users, hint: "Payslips, IR348, leave records", tone: "bg-indigo-50 border-indigo-200 text-indigo-900" },
    { key: "other", label: "Other document", icon: FileBox, hint: "Mortgage, insurance, anything else", tone: "bg-zinc-50 border-zinc-200 text-zinc-900" },
];

const ACCEPT = "image/*,application/pdf,.heic,.heif,.csv,.xls,.xlsx,.docx,.zip";

// Icon strings returned by /api/portal/checklist → lucide-react components.
const CHECKLIST_ICONS = {
    Landmark, FileText, Receipt: ReceiptIcon, Users, MapPin, FileSpreadsheet,
    Mail, FileBox,
};

// Tone → soft Tailwind card style. Same visual vocabulary as CATEGORIES tiles.
const TONE_STYLES = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber:   "bg-amber-50 border-amber-200 text-amber-900",
    sky:     "bg-sky-50 border-sky-200 text-sky-900",
    violet:  "bg-violet-50 border-violet-200 text-violet-900",
    rose:    "bg-rose-50 border-rose-200 text-rose-900",
    indigo:  "bg-indigo-50 border-indigo-200 text-indigo-900",
    zinc:    "bg-zinc-50 border-zinc-200 text-zinc-900",
};

// Checklist item key → document_type for the /documents/ endpoint. Anything
// not listed falls through to "other" — the backend still preserves the
// `checklist_item_key` on metadata for later resolution.
const CHECKLIST_KEY_TO_DOC_TYPE = {
    bank_statement:      "bank_statement",
    sales_invoice:       "invoice",
    income_records:      "invoice",
    expense_receipt:     "receipt",
    property_expenses:   "receipt",
    fuel_vehicle:        "receipt",
    wages_paye:          "payroll_report",
    rental_income:       "invoice",
    loan_statement:      "other",
    airbnb_statement:    "other",
    platform_summary:    "other",
    mileage_log:         "other",
    vehicle_log:         "other",
    vehicle_purchase:    "other",
    shareholder_details: "other",
};

async function sha256Hex(blob) {
    const buf = await blob.arrayBuffer();
    const hash = await crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

export default function PortalUpload() {
    const [params] = useSearchParams();
    const nav = useNavigate();
    const requestId = params.get("requestId") || "";
    const presetType = params.get("type") || "";
    const { activeClient, tenant } = useAuth();

    const [request, setRequest] = useState(null);
    const [active, setActive] = useState(presetType && CATEGORIES.find((c) => c.key === presetType) ? presetType : null);
    const [files, setFiles] = useState([]);    // { name, mime, size, data_base64, sha256, progress, status, error }
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [alias, setAlias] = useState(null);
    // Segment-driven checklist. `null` while loading; `{segment:null,…}` when
    // the client's portal_segment isn't set → we render the legacy tile grid.
    const [checklist, setChecklist] = useState(null);
    // The active checklist item (if any) — its key is passed as
    // `checklist_item_key` to the /documents/ endpoint so the backend can
    // resolve status for the item on next load.
    const [activeChecklistItem, setActiveChecklistItem] = useState(null);
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);

    useEffect(() => {
        api.get("/documents/intake/alias").then((r) => setAlias(r.data)).catch(() => {});
        loadChecklist();
    }, []);

    async function loadChecklist() {
        try {
            const { data } = await api.get("/portal/checklist");
            setChecklist(data);
        } catch {
            setChecklist({ segment: null, items: [], progress: { done: 0, total: 0 } });
        }
    }

    useEffect(() => {
        if (requestId) {
            api.get(`/requests/${requestId}`).then((r) => {
                setRequest(r.data);
                if (r.data.doc_types_required?.[0]) {
                    const mapped = CATEGORIES.find((c) => c.key === r.data.doc_types_required[0]);
                    if (mapped) setActive(mapped.key);
                }
            }).catch(() => {});
        }
    }, [requestId]);

    const openPicker = useCallback((cameraOnly = false) => {
        (cameraOnly ? cameraInputRef : fileInputRef).current?.click();
    }, []);

    function selectCategory(key) {
        setActive(key);
        setActiveChecklistItem(null);
        setDone(false);
        setTimeout(() => openPicker(false), 80);
    }

    // Tap a checklist item — behaves like selecting a category tile, but also
    // records the item so its status can be refreshed after upload.
    function selectChecklistItem(item) {
        const cat = CHECKLIST_KEY_TO_DOC_TYPE[item.key] || "other";
        setActiveChecklistItem(item);
        setActive(cat);
        setDone(false);
        setTimeout(() => openPicker(false), 80);
    }

    async function onFilesPicked(picked) {
        const newOnes = [];
        for (const f of picked) {
            if (f.size > 25 * 1024 * 1024) {
                toast.error(`${f.name} is over 25 MB`);
                continue;
            }
            // Compute sha256 client-side (faster UX, dedupe pre-check)
            const sha = await sha256Hex(f);
            const pre = await api.post("/documents/intake/dedupe-check", { sha256: sha }).catch(() => null);
            if (pre?.data?.duplicate) {
                toast.error(`"${f.name}" was already uploaded${pre.data.existing?.period_month ? ` for ${pre.data.existing.period_month}` : ""}.`);
                continue;
            }
            const b64 = await fileToBase64(f);
            newOnes.push({
                name: f.name,
                mime: f.type || "application/octet-stream",
                size: f.size,
                data_base64: b64,
                sha256: sha,
                progress: 0,
                status: "ready",
            });
        }
        if (newOnes.length === 0) return;
        setFiles((prev) => [...prev, ...newOnes]);
    }

    async function submit() {
        if (!active) { toast.error("Pick a category first"); return; }
        if (!files.length) { toast.error("Pick at least one file"); return; }
        if (!activeClient?.id) { toast.error("Pick an entity from the top-right switcher"); return; }
        setBusy(true);
        try {
            // Map category → doc_type (server also handles this; keeping consistent here)
            const docTypeMap = { bank_statement: "bank_statement", receipt: "receipt", invoice: "invoice", ird_letter: "tax_correspondence", gst: "gst_support", payroll: "payroll_report", other: "other" };
            await api.post("/documents/", {
                client_id: activeClient.id,
                request_id: requestId || undefined,
                document_type: docTypeMap[active] || "other",
                category_label: activeChecklistItem?.label || active,
                classification_source: "client_tile",
                source_channel: "portal",
                notes: notes || undefined,
                checklist_item_key: activeChecklistItem?.key || undefined,
                files: files.map((f) => ({ name: f.name, mime: f.mime, size: f.size, data_base64: f.data_base64, sha256: f.sha256, original_filename: f.name })),
            });
            setDone(true);
            setFiles([]);
            setNotes("");
            // Refresh checklist so the just-uploaded item flips to "uploaded" ✅
            loadChecklist();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.code === "duplicate") {
                toast.error(detail.message || "Already uploaded");
            } else {
                toast.error(formatApiError(e));
            }
        } finally {
            setBusy(false);
        }
    }

    if (done) {
        return (
            <div className="max-w-md mx-auto py-12 text-center" data-testid="upload-success">
                <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 grid place-items-center">
                    <CheckCircle2 className="h-9 w-9 text-emerald-600" />
                </div>
                <h2 className="font-display text-2xl mt-4">Document received</h2>
                <p className="text-sm text-[#5E636E] mt-2 leading-relaxed">
                    Your accountant has been notified. We&apos;ve filed it under <span className="font-medium text-[#0F1115]">{activeChecklistItem?.label || CATEGORIES.find((c) => c.key === active)?.label}</span> for {activeClient?.name}.
                </p>
                <div className="mt-6 flex flex-col gap-2">
                    <Button onClick={() => { setActive(null); setActiveChecklistItem(null); setDone(false); }} className="bg-[#0E3B2E] hover:bg-[#0B2F25]" data-testid="upload-another">Upload another</Button>
                    <Button variant="outline" onClick={() => nav("/portal/history")}>View timeline</Button>
                </div>
            </div>
        );
    }

    if (!active) {
        const useChecklist = !!checklist?.segment && (checklist.items?.length ?? 0) > 0;
        return (
            <div className="space-y-5">
                <div>
                    <div className="co-label">Upload</div>
                    <h1 className="font-display text-3xl tracking-tight font-medium mt-1">Send documents</h1>
                    <p className="text-sm text-[#5E636E] mt-2">{activeClient?.name || "Your business"} · {tenant?.name}</p>
                </div>

                {request && (
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4" data-testid="upload-request-context">
                        <div className="co-label !text-blue-700">Linked to request</div>
                        <div className="mt-1 font-medium text-blue-900">{request.title}</div>
                    </div>
                )}

                {useChecklist ? (
                    <ChecklistView checklist={checklist} onPick={selectChecklistItem} onPickOther={() => selectCategory("other")} />
                ) : (
                    <div>
                        <h2 className="text-sm font-medium text-[#5E636E] mb-3">Tap the kind of document you have</h2>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="upload-categories">
                            {CATEGORIES.map((c) => (
                                <button
                                    key={c.key}
                                    type="button"
                                    onClick={() => selectCategory(c.key)}
                                    className={`text-left p-4 rounded-xl border-2 hover:shadow-md hover:-translate-y-0.5 transition-all active:translate-y-0 ${c.tone}`}
                                    data-testid={`upload-tile-${c.key}`}
                                >
                                    <c.icon className="h-7 w-7" />
                                    <div className="mt-3 font-medium text-sm">{c.label}</div>
                                    <div className="mt-0.5 text-[11px] opacity-75 leading-tight">{c.hint}</div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {alias && (
                    <AliasCard alias={alias} client={activeClient} tenant={tenant} />
                )}

                <HelpFooter tenant={tenant} />
            </div>
        );
    }

    // Active category view — file picker + drop zone + queue
    const cat = CATEGORIES.find((c) => c.key === active);
    return (
        <div className="space-y-5">
            <button onClick={() => setActive(null)} className="inline-flex items-center gap-1 text-xs text-[#5E636E] hover:text-[#0F1115]" data-testid="upload-back">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to categories
            </button>
            <div className="flex items-center gap-3">
                <div className={`grid h-12 w-12 rounded-xl place-items-center border-2 ${cat.tone}`}>
                    <cat.icon className="h-6 w-6" />
                </div>
                <div>
                    <h1 className="font-display text-2xl font-medium leading-tight">{cat.label}</h1>
                    <p className="text-xs text-[#5E636E]">{cat.hint}</p>
                </div>
            </div>

            <div
                className="rounded-xl border-2 border-dashed border-[#D4D4D8] bg-[#FAFAFA] p-6 text-center cursor-pointer hover:bg-white transition-colors"
                onClick={() => openPicker(false)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); onFilesPicked(Array.from(e.dataTransfer.files)); }}
                data-testid="upload-dropzone"
            >
                <UploadIcon className="h-7 w-7 mx-auto text-[#5E636E]" />
                <div className="mt-2 text-base font-medium">Drop files here or tap to pick</div>
                <div className="text-xs text-[#5E636E] mt-1">PDF, JPG, PNG, HEIC, CSV, XLSX, DOCX, ZIP · up to 25 MB per file</div>
                <input ref={fileInputRef} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => onFilesPicked(Array.from(e.target.files || []))} data-testid="upload-file-input" />
                <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => onFilesPicked(Array.from(e.target.files || []))} data-testid="upload-camera-input" />
            </div>

            <div className="grid grid-cols-2 gap-2 sm:hidden">
                <Button variant="outline" onClick={() => openPicker(true)} data-testid="upload-camera">
                    <Camera className="h-4 w-4" /> Camera
                </Button>
                <Button variant="outline" onClick={() => openPicker(false)} data-testid="upload-files">
                    <Layers className="h-4 w-4" /> Files
                </Button>
            </div>

            {files.length > 0 && (
                <ul className="space-y-2" data-testid="upload-file-list">
                    {files.map((f, i) => (
                        <li key={i} className="flex items-center justify-between rounded-md border border-[#E4E4E7] bg-white px-3 py-2.5">
                            <span className="inline-flex items-center gap-2 min-w-0 flex-1">
                                <FileText className="h-4 w-4 text-[#5E636E] shrink-0" />
                                <span className="truncate text-sm">{f.name}</span>
                                <span className="shrink-0 text-[10px] font-mono text-[#A4A6AB]">{Math.round(f.size / 1024)} KB</span>
                            </span>
                            <button onClick={() => setFiles((p) => p.filter((_, idx) => idx !== i))} className="text-[#A4A6AB] hover:text-rose-600 ml-2" data-testid={`upload-remove-${i}`}>
                                <X className="h-4 w-4" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            <Textarea
                rows={2}
                placeholder="Anything we should know? (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="upload-notes"
            />

            <Button onClick={submit} disabled={busy || !files.length} className="w-full h-12 bg-[#0E3B2E] hover:bg-[#0B2F25] text-white text-base" data-testid="upload-submit">
                {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <><CheckCircle2 className="h-4 w-4" /> Submit {files.length > 1 ? `${files.length} files` : files.length === 1 ? "1 file" : ""}</>}
            </Button>
        </div>
    );
}


function AliasCard({ alias, client, tenant }) {
    const [showQr, setShowQr] = useState(false);

    const displayName = tenant?.name
        ? `${tenant.name} — Upload (${client?.name || "You"})`
        : `Upload documents — ${client?.name || "Your accountant"}`;

    function downloadVCard() {
        // vCard 3.0 — widest phone compatibility. iOS / Android both accept
        // this via a shared file → "Add contact" → the alias saves as email.
        const escape = (s) => (s || "").replace(/([\\;,])/g, "\\$1").replace(/\n/g, "\\n");
        const note = `Forward receipts, bank statements & IRD letters to this address. ${tenant?.name || "Your accountant"} auto-files them to ${client?.name || "your business"}.`;
        const parts = [
            "BEGIN:VCARD",
            "VERSION:3.0",
            `FN:${escape(displayName)}`,
            `N:${escape(displayName)};;;;`,
            tenant?.name ? `ORG:${escape(tenant.name)}` : null,
            `EMAIL;TYPE=INTERNET,PREF:${alias.alias}`,
            `NOTE:${escape(note)}`,
            "END:VCARD",
        ].filter(Boolean);
        const blob = new Blob([parts.join("\r\n") + "\r\n"], { type: "text/vcard;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const safeName = (client?.name || "upload").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 32) || "upload";
        a.download = `${safeName}-upload.vcf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        toast.success("Contact card saved — open it to add to your address book");
    }

    // mailto: link so the QR-code scan on a phone opens the mail app pre-addressed.
    const mailto = `mailto:${alias.alias}?subject=${encodeURIComponent(alias.magic_subject || "Documents")}`;

    return (
        <div className="mt-4 rounded-lg border border-[#E4E4E7] bg-white p-4" data-testid="upload-alias-card">
            <div className="flex items-start gap-3">
                <div className="grid h-9 w-9 place-items-center rounded-full bg-[#0E3B2E]/10 text-[#0E3B2E] shrink-0">
                    <Mail className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">Or email documents in</div>
                    <p className="text-xs text-[#5E636E] mt-0.5 leading-relaxed">
                        Forward any email with attachments — IRD letters, bank statements, insurance documents — to your private upload address.
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                        <code className="flex-1 truncate font-mono text-xs bg-[#F5F4EF] rounded px-2 py-1.5">{alias.alias}</code>
                        <button
                            type="button"
                            className="text-[#0E3B2E] hover:text-[#0B2F25] text-xs inline-flex items-center gap-1"
                            onClick={() => { navigator.clipboard?.writeText(alias.alias); toast.success("Copied"); }}
                            data-testid="upload-alias-copy"
                        >
                            <Copy className="h-3 w-3" /> Copy
                        </button>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={downloadVCard}
                            className="inline-flex items-center gap-1.5 rounded-full border border-[#E4E4E7] bg-white px-3 py-1.5 text-[11px] hover:bg-[#F5F4EF] transition-colors"
                            data-testid="upload-alias-save-contact"
                        >
                            <Contact className="h-3 w-3" /> Save to contacts
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowQr((v) => !v)}
                            className="inline-flex items-center gap-1.5 rounded-full border border-[#E4E4E7] bg-white px-3 py-1.5 text-[11px] hover:bg-[#F5F4EF] transition-colors"
                            data-testid="upload-alias-toggle-qr"
                        >
                            <QrCode className="h-3 w-3" /> {showQr ? "Hide QR" : "Show QR"}
                        </button>
                    </div>

                    {showQr && (
                        <div className="mt-3 flex items-start gap-4 rounded-lg border border-[#E4E4E7] bg-[#FAFAFA] p-3" data-testid="upload-alias-qr">
                            <div className="rounded-md bg-white p-2 shrink-0">
                                <QRCodeSVG value={mailto} size={128} level="M" includeMargin={false} />
                            </div>
                            <p className="text-[11px] text-[#5E636E] leading-snug">
                                Scan this QR with your phone camera to open a new email addressed to your private upload alias. Attach any receipt or bank statement and hit send — no login required.
                            </p>
                        </div>
                    )}

                    <p className="mt-2 text-[11px] text-[#A4A6AB]">
                        Subject can be anything — we&apos;ll auto-file it to <span className="font-medium text-[#0F1115]">{client?.name}</span>.
                    </p>
                </div>
            </div>
        </div>
    );
}


function ChecklistView({ checklist, onPick, onPickOther }) {
    const { items = [], progress = { done: 0, total: 0 }, segment_label } = checklist;
    const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

    return (
        <div data-testid="upload-checklist">
            <div className="flex items-baseline justify-between gap-3">
                <h2 className="text-sm font-medium text-[#0F1115]">Here&apos;s what we need from you</h2>
                <span className="text-[11px] text-[#5E636E] font-mono" data-testid="upload-checklist-progress">
                    {progress.done} of {progress.total} done
                </span>
            </div>
            {segment_label && (
                <p className="mt-1 text-[11px] text-[#5E636E]">
                    Tailored for <span className="font-medium text-[#0F1115]">{segment_label}</span>
                </p>
            )}

            <div className="mt-2 h-1.5 rounded-full bg-[#E4E4E7] overflow-hidden">
                <div
                    className="h-full bg-[#0E3B2E] transition-all duration-500"
                    style={{ width: `${pct}%` }}
                    data-testid="upload-checklist-progress-bar"
                />
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {items.map((it) => (
                    <ChecklistCard key={it.key} item={it} onPick={() => onPick(it)} />
                ))}
                {/* Always-visible "Other" fallback card at the end. */}
                <button
                    type="button"
                    onClick={onPickOther}
                    className="text-left p-4 rounded-xl border-2 bg-zinc-50 border-zinc-200 text-zinc-900 hover:shadow-md hover:-translate-y-0.5 transition-all active:translate-y-0"
                    data-testid="upload-checklist-other"
                >
                    <div className="flex items-start gap-3">
                        <div className="grid h-9 w-9 place-items-center rounded-full bg-white/70 shrink-0">
                            <Plus className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm">Other document</div>
                            <div className="mt-0.5 text-[11px] opacity-75 leading-snug">Anything else you&apos;d like to send</div>
                        </div>
                    </div>
                </button>
            </div>
        </div>
    );
}


function ChecklistCard({ item, onPick }) {
    const Icon = CHECKLIST_ICONS[item.icon] || FileText;
    const tone = TONE_STYLES[item.tone] || TONE_STYLES.zinc;
    return (
        <button
            type="button"
            onClick={onPick}
            className={`text-left p-4 rounded-xl border-2 hover:shadow-md hover:-translate-y-0.5 transition-all active:translate-y-0 ${tone}`}
            data-testid={`upload-checklist-item-${item.key}`}
        >
            <div className="flex items-start gap-3">
                <div className="grid h-9 w-9 place-items-center rounded-full bg-white/70 shrink-0">
                    <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                        <div className="font-medium text-sm">{item.label}</div>
                        <ChecklistStatusPill status={item.status} />
                    </div>
                    <div className="mt-0.5 text-[11px] opacity-80 leading-snug">{item.why}</div>
                    {item.upload_count > 0 && (
                        <div className="mt-2 text-[10px] font-mono opacity-70">
                            {item.upload_count} file{item.upload_count === 1 ? "" : "s"} on file
                        </div>
                    )}
                </div>
            </div>
        </button>
    );
}


function ChecklistStatusPill({ status }) {
    if (status === "uploaded") {
        return (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 border border-emerald-200 px-2 py-0.5 text-[10px] font-medium text-emerald-800" data-testid="checklist-status-uploaded">
                <CheckCircle2 className="h-2.5 w-2.5" /> Uploaded
            </span>
        );
    }
    if (status === "needs_review") {
        return (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 border border-amber-200 px-2 py-0.5 text-[10px] font-medium text-amber-800" data-testid="checklist-status-needs-review">
                <AlertTriangle className="h-2.5 w-2.5" /> Needs review
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 rounded-full bg-white/70 border border-black/10 px-2 py-0.5 text-[10px] font-medium text-[#5E636E]" data-testid="checklist-status-not-started">
            Not started
        </span>
    );
}


function HelpFooter({ tenant }) {
    // Prefer the tenant's support email (set in Firm settings); fall back to a
    // sensible default. Uses mailto: so we don't build a full contact form —
    // one clear action, matches the "no jargon" spec.
    const email = tenant?.support_email || tenant?.contact_email || "info@nztaxation.nz";
    return (
        <div className="mt-6 rounded-lg border border-[#E4E4E7] bg-[#FAFAFA] p-4" data-testid="upload-help-footer">
            <div className="flex items-start gap-3">
                <div className="grid h-9 w-9 place-items-center rounded-full bg-white text-[#0E3B2E] shrink-0">
                    <LifeBuoy className="h-4 w-4" />
                </div>
                <div className="flex-1 text-sm">
                    <div className="font-medium">Need help?</div>
                    <p className="text-xs text-[#5E636E] mt-0.5 leading-relaxed">
                        Not sure what to send, or having trouble with a file? Email
                        {" "}
                        <a
                            href={`mailto:${email}?subject=${encodeURIComponent(`Portal upload help — ${tenant?.name || ""}`)}`}
                            className="text-[#0E3B2E] font-medium underline underline-offset-2"
                            data-testid="upload-help-email"
                        >
                            {email}
                        </a>
                        {" "}and we&apos;ll come back to you the same business day.
                    </p>
                </div>
            </div>
        </div>
    );
}

